import * as moment from 'moment';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { IListItem, IWatrReimbursement, IWatrRequest } from '@nkr/models/index';
import { ReimbursementType } from '@nkr/enums/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Row, Well } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { DonorShieldContext } from './context';
import Header from './header';
import VerificationStepTracker from './verification-step-tracker';
import WatrInfoAccordions from './accordion';
import { getBankingReviewTerminology } from './banking-review-terminology';
import './view-donor-request-banking.css';

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
    countryList?: IListItem[],
    openReconcileReimbursementEntry?: boolean,
    onUpdate: (
        reqId: number, firstName: string, lastName: string, email: string, mobilePhone: string, dob: string, expirationDate: string,
        achAccountNumber: string, achRountingNumber: string, intBankingNumber: string, intSwiftNumber: string,
        nameOfBank: string, cityTownLocality: string, stateProvinceRegion: string, postalCode: string, additionalCode: string,
        bankCountry: number | null) => void,
}

interface IState {
    firstName: string,
    lastName: string,
    email: string,
    mobile: string,
    birthDate: string,
    expirationDate: string,
    achAccountNumber: string,
    achRoutingNumber: string,
    intlSwiftNumber: string,
    intlBankNumber: string,
    bankCountry: number | null,
    bankInformation: {
        nameOfBank: string,
        cityTownLocality: string,
        stateProvinceRegion: string,
        postalCode: string,
        additionalCode: string
    },
    reimbursements: IWatrReimbursement[]
}

const initialState: IState = {
    firstName: '',
    lastName: '',
    email: '',
    achAccountNumber: '',
    achRoutingNumber: '',
    intlSwiftNumber: '',
    intlBankNumber: '',
    bankCountry: null,
    mobile: '',
    birthDate: '',
    expirationDate: '',
    bankInformation: {
        nameOfBank: '',
        cityTownLocality: '',
        stateProvinceRegion: '',
        postalCode: '',
        additionalCode: ''
    },
    reimbursements: []
};

const reducer = (state: IState, action: any): IState => {
    switch (action.type) {
        case 'INITIALIZE':
            return { ...action.payload };

        case 'firstName':
        case 'lastName':
        case 'email':
        case 'mobile':
        case 'birthDate':
        case 'expirationDate':
        case 'achAccountNumber':
        case 'achRoutingNumber':
        case 'intlBankNumber':
        case 'intlSwiftNumber':
        case 'bankCountry':
            return { ...state, [action.type]: action.payload };

        case 'nameOfBank':
        case 'cityTownLocality':
        case 'stateProvinceRegion':
        case 'postalCode':
        case 'additionalCode':
            return { ...state, bankInformation: { ...state.bankInformation, [action.type]: action.payload } };
    }

    return state;
};

const ViewDonorRequestContent = (props: IProps) => {
    const [request, dispatch] = useReducer(reducer, initialState);
    const [savingWeekRequested, setSavingWeekRequested] = useState<number | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    const context = useContext(DonorShieldContext);

    const canEditOtherAmount = context.isNkrSuper && !!context.onUpdateRequestWeekOtherAmount;

    const handleSaveWeekOtherAmount = async (weekRequested: number, otherAmount: number) => {
        if (!context.onUpdateRequestWeekOtherAmount) return;
        setSavingWeekRequested(weekRequested);
        setSaveError(null);
        try {
            const response = await context.onUpdateRequestWeekOtherAmount(props.data.id, weekRequested, otherAmount);
            const res = response as any;
            if (!res?.data?.success && res?.data?.message) {
                setSaveError(res.data.message);
                toast.error(res.data.message);
            }
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'Failed to save other amount.';
            setSaveError(msg);
            toast.error(msg);
        } finally {
            setSavingWeekRequested(null);
        }
    };

    useEffect(() => {
        dispatch({
            type: 'INITIALIZE',
            payload: {
                firstName: props.data.firstName,
                lastName: props.data.lastName,
                email: props.data.email,
                mobile: props.data.mobile,
                birthDate: props.data.birthDate,
                expirationDate: props.data.expirationDate,
                achAccountNumber: props.data.achAccountNumber,
                achRoutingNumber: props.data.achRoutingNumber,
                intlBankNumber: props.data.intBankingNumber,
                intlSwiftNumber: props.data.intSwiftNumber,
                bankCountry: props.data.bankCountry ?? null,
                bankInformation: props.data.bankInformation,
                reimbursements: props.reimbursements
            }
        });
    }, [props.data])

    const getReimbursedTotals = () => {
        let wagesReimbursed = 0
        let totalReimbursed = 0;
        let travelReimbursed = 0;
        let dependentCareReimbursed = 0;

        for (var i = 0; i < request.reimbursements.length; i++) {
            let reimbursement = request.reimbursements[i];

            if (reimbursement.reimbursementType == ReimbursementType.TravelAndLodging) {
                travelReimbursed += reimbursement.amount;
            } else if (reimbursement.reimbursementType == ReimbursementType.Wages
                || reimbursement.reimbursementType == ReimbursementType.CreditReturnLostWageReimbursement) {
                wagesReimbursed += reimbursement.amount;
            } else if (reimbursement.reimbursementType == ReimbursementType.DependentCare) {
                dependentCareReimbursed += reimbursement.amount;
            }

            totalReimbursed += reimbursement.amount;
        }

        return { wagesReimbursed, totalReimbursed, travelReimbursed, dependentCareReimbursed };
    };

    const handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let { name, value } = e.currentTarget;

        dispatch({
            type: name,
            payload: name === 'bankCountry'
                ? (value !== '' ? parseInt(value, 10) : null)
                : value
        });
    };

    const handleRequestUpdate = () => {
        let { firstName, lastName, email, mobile, birthDate, expirationDate, achAccountNumber, achRoutingNumber, intlBankNumber, intlSwiftNumber, bankCountry, bankInformation } = request;

        if (expirationDate != null) {
            expirationDate = expirationDate.toString()
        }

        props.onUpdate(props.data.id, firstName, lastName,
            email, mobile, birthDate.toString(), expirationDate,
            achAccountNumber, achRoutingNumber,
            intlBankNumber, intlSwiftNumber,
            bankInformation?.nameOfBank, bankInformation?.cityTownLocality, bankInformation?.stateProvinceRegion, bankInformation?.postalCode, bankInformation?.additionalCode,
            bankCountry
        );
    };

    if (props.data == null)
        return null;

    let isUs = props.data.isUsBank != null
        ? props.data.isUsBank === true
        : (props.data.country == 0 ? false : true);

    let showIntlBankInfoAdminView = context.isNkrSuper && !isUs;

    const { wagesReimbursed, totalReimbursed, travelReimbursed, dependentCareReimbursed } = getReimbursedTotals();

    const bankingTerms = getBankingReviewTerminology(props.data.country, props.data.isUsBank);
    const bankingReviewMsg = (props.data.bankingRoutingRequiresReview || props.data.bankingAccountRequiresReview) ? (
        <Alert bsStyle="warning" style={{ marginBottom: 10 }}>
            <strong>Banking correction required:</strong>{' '}
            {props.data.bankingRoutingRequiresReview && <span>{bankingTerms.routingReviewAlert}</span>}
            {props.data.bankingAccountRequiresReview && <span>{bankingTerms.accountReviewAlert}</span>}
        </Alert>
    ) : null;

    return (
        <>
            <Header data={props.data} showStatus={false} />
            {bankingReviewMsg}

            <Alert bsStyle='info' style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <Row>
                            <Col xs={12} md={10} lg={10} style={{ fontWeight: 500 }}>Organ Type:</Col>
                            <Col xs={12} md={2} lg={2}>
                                {props.data.donationOrganType == 1 ? 'Liver' : 'Kidney'}
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={10} lg={10} style={{ fontWeight: 500 }}>Wages Reimbursed:</Col>
                            <Col xs={12} md={2} lg={2}>${wagesReimbursed.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={10} lg={10} style={{ fontWeight: 500 }}>Days Reimbursed:</Col>
                            <Col xs={12} md={2} lg={2}>{props.data.totalWagesReimbursedDays}</Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={10} lg={10} style={{ fontWeight: 500 }}>Expense Reimbursed:</Col>
                            <Col xs={12} md={2} lg={2}>${travelReimbursed.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={10} lg={10} style={{ fontWeight: 500 }}>Dependent Care Reimbursed:</Col>
                            <Col xs={12} md={2} lg={2}>${dependentCareReimbursed.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Col>
                        </Row>

                        <Row>
                            <Col xs={12} lg={10} style={{ fontWeight: 500 }}>Total Reimbursed:</Col>
                            <Col xs={12} md={2} lg={2}>${totalReimbursed.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Col>
                        </Row>

                        {context.isNkrSuper &&
                            <Row>
                                <Col xs={12} lg={10} style={{ fontWeight: 500 }}>Don't Expire Until:</Col>
                                <Col xs={12} md={2} lg={2}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ marginRight: 5 }}>
                                            <FormControl
                                                id='expirationDate'
                                                name='expirationDate'
                                                type='date'
                                                bsSize='sm'
                                                value={moment(request.expirationDate)?.format('YYYY-MM-DD')}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        }
                    </div>
                </div>
            </Alert>

            <Row>
                <Col sm={12} md={6}>
                    <ControlLabel>First Name</ControlLabel>{' '}
                    <FormControl
                        type='text'
                        name='firstName'
                        value={request.firstName}
                        onChange={handleInputChange} />{' '}
                </Col>

                <Col sm={12} md={6}>
                    <FormGroup>
                        <ControlLabel>Last Name</ControlLabel>{' '}
                        <FormControl
                            type='text'
                            name='lastName'
                            value={request.lastName}
                            onChange={handleInputChange} />
                    </FormGroup>{' '}
                </Col>
            </Row>

            <Row>
                <Col sm={12} md={5}>
                    <FormGroup>
                        <ControlLabel>Email</ControlLabel>{' '}
                        <FormControl
                            type='email'
                            name='email'
                            value={request.email}
                            onChange={handleInputChange} />
                    </FormGroup>{' '}
                </Col>
                <Col sm={12} md={3}>
                    <FormGroup>
                        <ControlLabel>Mobile Phone</ControlLabel>{' '}
                        <FormControl
                            type='text'
                            name='mobile'
                            maxLength={10}
                            value={request.mobile}
                            onChange={handleInputChange} />
                    </FormGroup>{' '}
                </Col>

                <Col sm={12} md={4}>
                    <ControlLabel>Birth Date</ControlLabel>{' '}
                    <FormControl
                        name='birthDate'
                        type='date'
                        max='9999-01-01'
                        value={moment(request.birthDate).format('YYYY-MM-DD')}
                        onChange={handleInputChange}
                    />
                </Col>
            </Row>

            {context.isNkrSuper && (
                <Well className='watr-edit-banking'>
                    <div className='watr-edit-banking__heading'>Bank Information</div>

                    {!showIntlBankInfoAdminView ? (
                        <Row className='watr-edit-banking__row'>
                            <Col sm={12} md={6}>
                                <div className='watr-edit-banking__field'>
                                    <label className='watr-edit-banking__label' htmlFor='achRoutingNumber'>ACH Routing #</label>
                                    <FormControl
                                        id='achRoutingNumber'
                                        type='text'
                                        name='achRoutingNumber'
                                        maxLength={9}
                                        value={request.achRoutingNumber}
                                        onChange={handleInputChange}
                                        disabled={!isUs}
                                    />
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className='watr-edit-banking__field'>
                                    <label className='watr-edit-banking__label' htmlFor='achAccountNumber'>ACH Account #</label>
                                    <FormControl
                                        id='achAccountNumber'
                                        type='text'
                                        name='achAccountNumber'
                                        value={request.achAccountNumber}
                                        maxLength={24}
                                        onChange={handleInputChange}
                                        disabled={!isUs}
                                    />
                                </div>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row className='watr-edit-banking__row'>
                                <Col sm={12} md={6}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='intlBankNumber'>International Bank #</label>
                                        <FormControl
                                            id='intlBankNumber'
                                            type='text'
                                            name='intlBankNumber'
                                            maxLength={24}
                                            value={request.intlBankNumber}
                                            onChange={handleInputChange}
                                            disabled={isUs}
                                        />
                                    </div>
                                </Col>
                                <Col sm={12} md={6}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='intlSwiftNumber'>Swift #</label>
                                        <FormControl
                                            id='intlSwiftNumber'
                                            type='text'
                                            name='intlSwiftNumber'
                                            value={request.intlSwiftNumber}
                                            maxLength={24}
                                            onChange={handleInputChange}
                                            disabled={isUs}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row className='watr-edit-banking__row'>
                                <Col sm={12} md={12}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='nameOfBank'>Name of Bank</label>
                                        <FormControl
                                            id='nameOfBank'
                                            type='text'
                                            name='nameOfBank'
                                            value={request.bankInformation.nameOfBank}
                                            maxLength={24}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row className='watr-edit-banking__row'>
                                <Col sm={12} md={4}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='cityTownLocality'>City/Town/Locality</label>
                                        <FormControl
                                            id='cityTownLocality'
                                            type='text'
                                            name='cityTownLocality'
                                            value={request.bankInformation.cityTownLocality}
                                            maxLength={24}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </Col>
                                <Col sm={12} md={4}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='stateProvinceRegion'>State/Province/Region</label>
                                        <FormControl
                                            id='stateProvinceRegion'
                                            type='text'
                                            name='stateProvinceRegion'
                                            value={request.bankInformation.stateProvinceRegion}
                                            maxLength={24}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </Col>
                                <Col sm={12} md={4}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='postalCode'>Postal Code</label>
                                        <FormControl
                                            id='postalCode'
                                            type='text'
                                            name='postalCode'
                                            value={request.bankInformation?.postalCode}
                                            maxLength={24}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row className='watr-edit-banking__row'>
                                <Col sm={12} md={6}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='bankCountry'>Bank Country</label>
                                        <FormControl
                                            id='bankCountry'
                                            componentClass='select'
                                            name='bankCountry'
                                            value={request.bankCountry ?? ''}
                                            onChange={handleInputChange}
                                        >
                                            <option value=''>- Please Select -</option>
                                            {(props.countryList || []).map((item, index) =>
                                                <option key={'bank-country-' + index} value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                    </div>
                                </Col>
                                <Col sm={12} md={6}>
                                    <div className='watr-edit-banking__field'>
                                        <label className='watr-edit-banking__label' htmlFor='additionalCode'>Additional Code</label>
                                        <FormControl
                                            id='additionalCode'
                                            type='text'
                                            name='additionalCode'
                                            value={request.bankInformation?.additionalCode}
                                            maxLength={24}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Well>
            )}

            <Row>
                <Col sm={12} md={12} className='text-center'>
                    <Button bsStyle='primary' onClick={handleRequestUpdate}>
                        Update Request
                    </Button>
                </Col>
            </Row>

            <br />

            <Row>
                <Col sm={12} md={12}>
                    <WatrInfoAccordions
                        data={props.data}
                        isAudit={props.isAudit}
                        reimbursements={props.reimbursements}
                        openReconcileReimbursementEntry={props.openReconcileReimbursementEntry}
                        allowEditWeekOtherAmount={canEditOtherAmount}
                        onWeekOtherAmountChange={() => {}}
                        onSaveWeekOtherAmount={canEditOtherAmount ? handleSaveWeekOtherAmount : undefined}
                        savingWeekRequested={savingWeekRequested}
                        saveError={saveError}
                    />
                </Col>
            </Row>
        </>
    );
}

export default ViewDonorRequestContent;